/**
 * API client for pro-api (BFF service)
 * Use this for business logic, keep direct user-service calls for auth
 */

import { messageFromApiErrorPayload } from './parse-api-error';
import { handleSessionExpired } from '../auth/session-expired';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export interface ApiError {
  error: string;
  status?: number;
}

export class ProApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any,
  ) {
    super(message);
    this.name = 'ProApiClientError';
  }
}

/**
 * Base fetch wrapper with error handling
 */
export async function proApiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ProApiClientError(
      messageFromApiErrorPayload(data) ||
        response.statusText ||
        'Request failed',
      response.status,
      data,
    );
  }

  return data as T;
}

function authenticatedProApiFetchOnce<T>(
  endpoint: string,
  accessToken: string,
  options: RequestInit = {},
): Promise<T> {
  return proApiFetch<T>(endpoint, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * Authenticated fetch that refreshes the access token once on 401 and retries.
 * If refresh fails, clears the session and redirects to login immediately.
 */
export async function authenticatedProApiFetch<T>(
  endpoint: string,
  accessToken: string,
  options: RequestInit = {},
): Promise<T> {
  try {
    return await authenticatedProApiFetchOnce<T>(endpoint, accessToken, options);
  } catch (err) {
    if (!(err instanceof ProApiClientError) || err.status !== 401) {
      throw err;
    }

    let newToken: string | null = null;
    try {
      const { refreshAccessTokenForProApi } = await import('./pro-api-auth');
      newToken = await refreshAccessTokenForProApi();
    } catch {
      newToken = null;
    }

    if (!newToken) {
      handleSessionExpired();
      throw err;
    }

    return authenticatedProApiFetchOnce<T>(endpoint, newToken, options);
  }
}

/**
 * @deprecated Use authenticatedProApiFetch — it always retries once on 401.
 */
export const authenticatedProApiFetchWithRetry = authenticatedProApiFetch;
